import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Divider,
  makeStyles,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";

import { Card, CardContent, CardActions } from "../../components/Card";
import divisions from "../../divisions.json";
import Button from "../../components/Button";
import { TextInput, Select } from "../../components/Form";
import { useAlert } from "../../components/Alert";
import Table from "../../components/Table";
import LoadingModal from "../../components/LoadingModal";

const GET_VENDORS = gql`
  query vendors(
    $searchTerm: String
    $latest: Boolean
    $pageSize: Int
    $page: Int
  ) {
    vendors(
      searchTerm: $searchTerm
      latest: $latest
      pageSize: $pageSize
      page: $page
    ) {
      count
      pageCount
      contents {
        id
        name
        type
        mobile1
        mobile2
        fax
        email
        vatNumber
        url
        division
        subdivision
        address
        paymentMethod
        companyName
        account
        remark
      }
    }
  }
`;

const GET_VENDOR = gql`
  query vendor($id: Int!) {
    vendor(id: $id) {
      id
      name
      type
      mobile1
      mobile2
      fax
      email
      vatNumber
      url
      division
      subdivision
      address
      paymentMethod
      companyName
      account
      remark
    }
  }
`;

const SAVE_VENDOR = gql`
  mutation saveVendor($vendorInput: VendorInput!) {
    saveVendor(vendorInput: $vendorInput)
  }
`;

const DELETE_VENDOR = gql`
  mutation deleteVendor($id: Int!) {
    deleteVendor(id: $id)
  }
`;

export default function VendorContactTable() {
  const Alert = useAlert();
  const useStyles = makeStyles({
    DocumentContainerEnd: {
      display: "flex",
      alignItems: "flex-end",
    },
  });
  const classes = useStyles();

  const [newData, setNewData] = useState({
    searchTerm: "",
    open: false,
    page: 1,
    id: null,
  });

  function changeDataListener(key, v) {}
  function changeData(key, v, rerender = false) {
    if (rerender) setNewData((prev) => ({ ...prev, [key]: v }));
    else setNewData((prev) => Object.assign(prev, { [key]: v }));
    if (typeof changeDataListener === "function") changeDataListener(key, v);
  }

  const { data, refetch, loading } = useQuery(GET_VENDORS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      searchTerm: newData.searchTerm,
      latest: true,
      pageSize: 10,
      page: newData.page,
    },
    onError(error) {
      Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
    },
  });

  const [deleteVendor, { loading: deleteVendorLoading }] = useMutation(
    DELETE_VENDOR,
    {
      onCompleted({ deleteVendor }) {
        if (deleteVendor) {
          return Alert.alert("", "刪除成功！", [
            {
              text: "確定",
              onPress: refetch,
              type: "ok",
            },
          ]);
        } else {
          return Alert.alert("", "刪除失敗！", [
            {
              text: "確定",
              type: "ok",
            },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    }
  );

  return (
    <Card>
      <LoadingModal loading={deleteVendorLoading} />
      <CardContent>
        <Grid container spacing={1} justify="space-between">
          <Grid item>
            <Typography color="primary">廠商查詢</Typography>
            <TextInput
              value={newData.searchTerm}
              onChange={(v) => {
                changeData("search", v, true);
              }}
            />
          </Grid>
          <Grid item classes={{ root: classes.DocumentContainerEnd }}>
            <Button
              color="primary"
              variant="contained"
              onPress={() => {
                changeData("open", true);
                changeData("id", null, true);
              }}
            >
              新增供應商
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent>
        <Table
          data={data?.vendors.contents}
          tableWidth={2}
          header={[
            "供應商名稱",
            "供應商簡介",
            "供應商類別",
            "供應商電話",
            "操作",
          ]}
          columns={[
            (item) => item.name,
            (item) => item.remark,
            (item) => item.type,
            (item) =>
              `${
                item.mobile2 ? item.mobile1 + "\n" + item.mobile2 : item.mobile1
              }`,
            (item) => (
              <Button
                variant="contained"
                color="primary"
                onPress={() => {
                  Alert.alert("通知", "確定要刪除？", [
                    {
                      text: "確定",
                      onPress: () => {
                        deleteVendor({ variables: { id: item.id } });
                      },
                      type: "ok",
                    },
                    {
                      text: "取消",
                      type: "cancel",
                    },
                  ]);
                }}
              >
                刪除
              </Button>
            ),
          ]}
          onPress={(item) => {
            changeData("open", true);
            changeData("id", item.id, true);
          }}
        />
        <VendorContactDialog
          open={newData.open}
          id={newData.id}
          onClose={(v) => {
            if (v === "refetch") refetch();
            changeData("open", false, true);
          }}
        />
      </CardContent>
      <CardActions>
        <Grid container justifyContent="center">
          <Pagination
            count={data?.vendors.pageCount ? data?.vendors.pageCount : 1}
            size="large"
            color="primary"
            page={newData.page}
            onChange={(e, n) => {
              changeData("page", n, true);
            }}
          />
        </Grid>
      </CardActions>
    </Card>
  );
}

function VendorContactDialog({ open, id, onClose = () => {} }) {
  const Alert = useAlert();
  const [newData, setNewData] = useState({
    name: "",
    type: "",
    mobile1: "",
    mobile2: "",
    fax: "",
    email: "",
    url: "",
    vatNumber: "",
    divisionArray: [],
    division: "null",
    divisionObject: { value: "null", label: "全部" },
    subdivisionsArray: [],
    subdivisions: "",
    subdivisionsObject: {},
    address: "",
    paymentMethod: "",
    account: "",
    companyName: "",
    remark: "",
  });
  const subdivisionsRef = useRef("");
  function changeDataListener(key, v) {
    if (key === "division") {
      if (Boolean(v) && v !== "null") {
        const subdivisionsKey = v && Object.keys(divisions[v].subdivisions);
        const newSubdivisionsArray = [];
        subdivisionsKey.map((item) => {
          newSubdivisionsArray.push({
            value: item,
            label: divisions[v].subdivisions[item].zhTW,
          });
        });
        if (Boolean(subdivisionsRef.current)) {
          const hasSubdivisions = newSubdivisionsArray.find(
            (item) => item.value == subdivisionsRef.current
          );
          if (hasSubdivisions) {
            changeData("subdivisions", hasSubdivisions.value);
            changeData("subdivisionsObject", hasSubdivisions, true);
          }
        }
        changeData("subdivisionsArray", newSubdivisionsArray, true);
      } else {
        subdivisionsRef.current = "";
        changeData("subdivisionsArray", []);
        changeData("subdivisions", "");
        changeData("subdivisionsObject", {}, true);
      }
    }
  }
  function changeData(key, v, rerender = false) {
    if (rerender) setNewData((prev) => ({ ...prev, [key]: v }));
    else setNewData((prev) => Object.assign(prev, { [key]: v }));
    if (typeof changeDataListener === "function") changeDataListener(key, v);
  }

  const [getVendor, { loading }] = useLazyQuery(GET_VENDOR, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted({ vendor }) {
      if (vendor) {
        changeData("name", vendor.name);
        changeData("type", vendor.type);
        changeData("mobile1", vendor.mobile1);
        changeData("mobile2", vendor.mobile2);
        changeData("fax", vendor.fax);
        changeData("email", vendor.email);
        changeData("vatNumber", vendor.vatNumber);
        changeData("url", vendor.url);
        if (vendor.division) {
          changeData("divisionObject", {
            label: divisions[vendor.division]["zhTW"],
            value: vendor.division,
          });
          changeData("division", vendor.division);
        }
        if (vendor.subdivision) {
          changeData("subdivisionsObject", {
            label:
              divisions[vendor.division]["subdivisions"][vendor.subdivision][
                "zhTW"
              ],
            value: vendor.subdivision,
          });
          changeData("subdivisions", vendor.subdivision);
        }
        changeData("address", vendor.address);
        changeData("paymentMethod", vendor.paymentMethod);
        changeData("companyName", vendor.companyName);
        changeData("account", vendor.account);
        changeData("remark", vendor.remark, true);
      }
    },
    onError(error) {
      Alert.alert("通知", `${error}`, [
        {
          text: "確定",
          type: "ok",
        },
      ]);
    },
  });

  const [saveVendor, { loading: saveVendorLoading }] = useMutation(
    SAVE_VENDOR,
    {
      onCompleted({ saveVendor }) {
        if (saveVendor) {
          return Alert.alert("", "儲存成功！", [
            {
              text: "確定",
              onPress: onClose("refetch"),
              type: "ok",
            },
          ]);
        } else {
          return Alert.alert("", "刪除失敗！", [
            {
              text: "確定",
              type: "ok",
            },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    }
  );

  function save() {
    const vendorInput = {
      name: newData.name,
      type: newData.type,
      mobile1: newData.mobile1,
      mobile2: newData.mobile2,
      fax: newData.fax,
      email: newData.email,
      vatNumber: newData.vatNumber,
      url: newData.url,
      division: newData.division,
      subdivision: newData.subdivisions,
      address: newData.address,
      paymentMethod: newData.paymentMethod,
      companyName: newData.companyName,
      account: newData.account,
      remark: newData.remark,
    };
    if (id) vendorInput.id = id;

    saveVendor({ variables: { vendorInput } });
  }
  useEffect(() => {
    const divisionKey = Object.keys(divisions);
    const newDivisionArray = [
      {
        value: "null",
        label: "全部",
      },
    ];
    divisionKey.map((item) => {
      newDivisionArray.push({
        value: item,
        label: divisions[item].zhTW,
      });
    });
    changeData("divisionArray", newDivisionArray, true);
  }, []);

  useEffect(() => {
    if (!!id) getVendor({ variables: { id } });
  }, [id]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"md"}>
      <LoadingModal loading={saveVendorLoading || loading} />
      <DialogTitle>新增供應商</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} direction="row">
          <Grid item sm={4}>
            <Typography color="primary">供應商名稱</Typography>
            <TextInput
              value={newData.name}
              onChange={(v) => {
                changeData("name", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">分類</Typography>
            <TextInput
              value={newData.type}
              onChange={(v) => {
                changeData("type", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">電話1</Typography>
            <TextInput
              value={newData.mobile1}
              onChange={(v) => {
                changeData("mobile1", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">電話2</Typography>
            <TextInput
              value={newData.mobile2}
              onChange={(v) => {
                changeData("mobile2", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">傳真</Typography>
            <TextInput
              value={newData.fax}
              onChange={(v) => {
                changeData("fax", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">e-mail</Typography>
            <TextInput
              value={newData.email}
              onChange={(v) => {
                changeData("email", v, true);
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Typography color="primary">統一編號</Typography>
            <TextInput
              value={newData.vatNumber}
              onChange={(v) => {
                changeData("vatNumber", v, true);
              }}
            />
          </Grid>
          <Grid item sm={8}>
            <Typography color="primary">網址</Typography>
            <TextInput
              value={newData.url}
              onChange={(v) => {
                changeData("url", v, true);
              }}
              fullWidth
            />
          </Grid>
        </Grid>
        {"\u2002"}
        <Divider />
        <Grid container spacing={1}>
          <Grid item xs={4} sm={2}>
            <Typography color="primary" style={{ fontSize: 15 }}>
              縣市
            </Typography>
            <Select
              placeholder={"請選擇縣市"}
              items={newData.divisionArray}
              value={
                newData.divisionObject.value ? newData.divisionObject : null
              }
              onChange={(e, value) => {
                if (value) {
                  changeData("division", value.value);
                  changeData("divisionObject", value, true);
                } else {
                  changeData("division", "null");
                  changeData(
                    "divisionObject",
                    { value: "null", label: "全部" },
                    true
                  );
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <Typography color="primary" style={{ fontSize: 15 }}>
              區域
            </Typography>
            <Select
              placeholder={"請選擇地區"}
              items={newData.subdivisionsArray}
              value={
                newData.subdivisionsObject.value
                  ? newData.subdivisionsObject
                  : null
              }
              onChange={(e, value) => {
                if (value) {
                  changeData("subdivisions", value.value);
                  changeData("subdivisionsObject", value, true);
                } else {
                  changeData("subdivisions", null);
                  changeData("subdivisionsObject", {}, true);
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <Typography color="primary">地址</Typography>
            <TextInput
              value={newData.address}
              onChange={(v) => {
                changeData("address", v, true);
              }}
              fullWidth
            />
          </Grid>
        </Grid>
        {"\u2002"}
        <Divider />
        <Typography style={{ fontSize: 20 }}>付款資訊</Typography>
        <Grid container spacing={1}>
          <Grid item>
            <Typography color="primary">方式</Typography>
            <TextInput
              value={newData.paymentMethod}
              onChange={(v) => {
                changeData("paymentMethod", v, true);
              }}
            />
          </Grid>
          <Grid item>
            <Typography color="primary">抬頭</Typography>
            <TextInput
              value={newData.companyName}
              onChange={(v) => {
                changeData("companyName", v, true);
              }}
            />
          </Grid>
          <Grid item>
            <Typography color="primary">帳號</Typography>
            <TextInput
              value={newData.account}
              onChange={(v) => {
                changeData("account", v, true);
              }}
            />
          </Grid>
        </Grid>
        {"\u2002"}
        <Divider />
        <Grid item xs={12} sm={12}>
          <Typography color="primary">備註</Typography>
          <TextInput
            value={newData.remark}
            multiline
            rows={5}
            onChange={(v) => {
              changeData("remark", v, true);
            }}
            fullWidth
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={1} justify="center">
          <Grid item>
            <Button color="primary" variant="contained" onPress={save}>
              送出
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="outlined"
              onPress={() => {
                onClose();
              }}
            >
              返回
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
